// MentorTrainingDashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

function flagLabel(flag) {
  switch (flag) {
    case 'ready_for_supervised':
      return 'Ready for supervised';
    case 'ready_for_full':
      return 'Ready for full teacher';
    case 'stuck_in_shadow':
      return 'Stuck in shadow';
    case 'stuck_in_supervised':
      return 'Stuck in supervised';
    case 'low_exposure':
      return 'Low exposure';
    case 'mentor_flagged':
      return 'Mentor flagged';
    case 'mentor_recommends_supervised':
      return 'Mentor: promote to supervised';
    case 'mentor_recommends_independent':
      return 'Mentor: ready for independent';
    case 'ok':
    default:
      return 'OK';
  }
}

function flagColor(flag) {
  switch (flag) {
    case 'ready_for_supervised':
    case 'ready_for_full':
    case 'mentor_recommends_supervised':
    case 'mentor_recommends_independent':
      return 'bg-blue-100 text-blue-700';
    case 'stuck_in_shadow':
    case 'stuck_in_supervised':
      return 'bg-orange-100 text-orange-700';
    case 'low_exposure':
    case 'mentor_flagged':
      return 'bg-red-100 text-red-700';
    case 'ok':
    default:
      return 'bg-green-100 text-green-700';
  }
}

const DEFAULT_TRAINING_RULES = {
  shadow: { min_visits: 20, min_days: 14 },
  supervised: { min_visits: 40, min_days: 30 },
  min_monthly_visits: 12,
  stuck_after_days: 60,
};

function safeInt(v, fallback = 0) {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.round(n));
}

function normalizeTrainingRules(input) {
  const parsed = input || {};
  return {
    shadow: {
      min_visits:
        safeInt(parsed?.shadow?.min_visits, DEFAULT_TRAINING_RULES.shadow.min_visits) ||
        DEFAULT_TRAINING_RULES.shadow.min_visits,
      min_days:
        safeInt(parsed?.shadow?.min_days, DEFAULT_TRAINING_RULES.shadow.min_days) ||
        DEFAULT_TRAINING_RULES.shadow.min_days,
    },
    supervised: {
      min_visits:
        safeInt(parsed?.supervised?.min_visits, DEFAULT_TRAINING_RULES.supervised.min_visits) ||
        DEFAULT_TRAINING_RULES.supervised.min_visits,
      min_days:
        safeInt(parsed?.supervised?.min_days, DEFAULT_TRAINING_RULES.supervised.min_days) ||
        DEFAULT_TRAINING_RULES.supervised.min_days,
    },
    min_monthly_visits:
      safeInt(parsed?.min_monthly_visits, DEFAULT_TRAINING_RULES.min_monthly_visits) ||
      DEFAULT_TRAINING_RULES.min_monthly_visits,
    stuck_after_days:
      safeInt(parsed?.stuck_after_days, DEFAULT_TRAINING_RULES.stuck_after_days) ||
      DEFAULT_TRAINING_RULES.stuck_after_days,
  };
}

function deriveRulesStatus(t, rules) {
  const stage = String(t?.training_stage || '').toLowerCase();
  const days = safeInt(t?.days_in_stage, 0);
  const visitsInStage = safeInt(t?.visits_in_stage, 0);
  const visits30 = safeInt(t?.visits_last_30, 0);

  const isShadow = stage === 'shadow';
  const isSupervised = stage === 'supervised';

  const stuckAfter = safeInt(rules?.stuck_after_days, DEFAULT_TRAINING_RULES.stuck_after_days);
  const minMonthly = safeInt(rules?.min_monthly_visits, DEFAULT_TRAINING_RULES.min_monthly_visits);

  const needVisits = isShadow
    ? safeInt(rules?.shadow?.min_visits, DEFAULT_TRAINING_RULES.shadow.min_visits)
    : isSupervised
    ? safeInt(rules?.supervised?.min_visits, DEFAULT_TRAINING_RULES.supervised.min_visits)
    : 0;

  const needDays = isShadow
    ? safeInt(rules?.shadow?.min_days, DEFAULT_TRAINING_RULES.shadow.min_days)
    : isSupervised
    ? safeInt(rules?.supervised?.min_days, DEFAULT_TRAINING_RULES.supervised.min_days)
    : 0;

  const meetsThresholds =
    (needVisits > 0 ? visitsInStage >= needVisits : true) &&
    (needDays > 0 ? days >= needDays : true);

  if ((isShadow || isSupervised) && days >= stuckAfter) {
    return { flag: isShadow ? 'stuck_in_shadow' : 'stuck_in_supervised', needVisits, needDays, visitsInStage, days, visits30, minMonthly };
  }

  if ((isShadow || isSupervised) && days >= 7 && visits30 < minMonthly) {
    return { flag: 'low_exposure', needVisits, needDays, visitsInStage, days, visits30, minMonthly };
  }

  if (isShadow && meetsThresholds) {
    return { flag: 'ready_for_supervised', needVisits, needDays, visitsInStage, days, visits30, minMonthly };
  }
  if (isSupervised && meetsThresholds) {
    return { flag: 'ready_for_full', needVisits, needDays, visitsInStage, days, visits30, minMonthly };
  }

  return { flag: 'ok', needVisits, needDays, visitsInStage, days, visits30, minMonthly };
}

export default function MentorTrainingDashboard() {
  const [mentors, setMentors] = useState([]);
  const [selectedMentorId, setSelectedMentorId] = useState(null);
  const [trainees, setTrainees] = useState([]);
  const [loadingMentors, setLoadingMentors] = useState(false);
  const [loadingTrainees, setLoadingTrainees] = useState(false);

  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [traineeVisits, setTraineeVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [visitsPeriod, setVisitsPeriod] = useState('all');

  const [trainingRules, setTrainingRules] = useState(DEFAULT_TRAINING_RULES);
  const [loadingRules, setLoadingRules] = useState(false);

  useEffect(() => {
    // Ako imaš /training-rules-settings koristićemo ga, ali primarno ćemo uzeti meta iz trainees rute
    loadTrainingRulesBestEffort();
    loadMentors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedMentorId) {
      loadTrainees(selectedMentorId);
    } else {
      setTrainees([]);
      setSelectedTrainee(null);
      setTraineeVisits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMentorId]);

  useEffect(() => {
    if (selectedTrainee) {
      loadTraineeVisits(selectedTrainee.id, visitsPeriod);
    } else {
      setTraineeVisits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrainee, visitsPeriod]);

  async function loadTrainingRulesBestEffort() {
    try {
      setLoadingRules(true);
      const trRes = await API.get('/training-rules-settings');
      const d = trRes.data || {};
      let parsed = d;
      if (d && typeof d.value_text === 'string') {
        try {
          parsed = JSON.parse(d.value_text);
        } catch {
          parsed = DEFAULT_TRAINING_RULES;
        }
      }
      setTrainingRules(normalizeTrainingRules(parsed));
    } catch (err) {
      // ok, možda nema tu rutu – meta iz trainees će preuzeti
    } finally {
      setLoadingRules(false);
    }
  }

  async function loadMentors() {
    try {
      setLoadingMentors(true);
      const res = await API.get('/admin/training/mentors');
      setMentors(res.data || []);
      if (res.data && res.data.length > 0) {
        setSelectedMentorId(res.data[0].mentor_id);
      }
    } catch (err) {
      console.error('loadMentors error', err);
    } finally {
      setLoadingMentors(false);
    }
  }

  async function loadTrainees(mentorId) {
    try {
      setLoadingTrainees(true);
      const res = await API.get(`/admin/training/mentor/${mentorId}/trainees`);

      // NEW: backend returns { meta, trainees }
      const list = res.data?.trainees || res.data || [];
      setTrainees(list);

      // prefer backend meta rules (same as used to compute flags)
      const rulesFromMeta = res.data?.meta?.training_rules;
      if (rulesFromMeta) {
        setTrainingRules(normalizeTrainingRules(rulesFromMeta));
      }

      setSelectedTrainee(null);
      setTraineeVisits([]);
    } catch (err) {
      console.error('loadTrainees error', err);
    } finally {
      setLoadingTrainees(false);
    }
  }

  async function loadTraineeVisits(teacherId, period) {
    try {
      setLoadingVisits(true);
      const res = await API.get(`/admin/training/trainee/${teacherId}/visits`, {
        params: { period },
      });
      setTraineeVisits(res.data || []);
    } catch (err) {
      console.error('loadTraineeVisits error', err);
    } finally {
      setLoadingVisits(false);
    }
  }

  function formatDateYYYYMMDD(value) {
    if (!value) return '';
    const s = String(value);
    if (s.length >= 10 && s.includes('-')) return s.slice(0, 10);
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
    return d.toISOString().slice(0, 10);
  }

  const rulesByTraineeId = useMemo(() => {
    const map = new Map();
    (trainees || []).forEach((t) => {
      map.set(t.id, deriveRulesStatus(t, trainingRules));
    });
    return map;
  }, [trainees, trainingRules]);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold mb-1">Mentor &amp; Training Overview</h1>
          <p className="text-xs text-gray-500">
            Visits columns count <b>attended complete visits</b> (training attendance + independent visits). Flags are computed on backend using training_rules + mentor feedback.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border p-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-base">Training rules (organization)</h2>
          {loadingRules && <span className="text-xs text-gray-500">Loading rules…</span>}
        </div>

        <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
          <div className="border rounded-lg p-2 bg-gray-50">
            <div className="font-semibold text-gray-800 mb-1">Shadow → Supervised</div>
            <div className="text-gray-700">Min visits: <b>{trainingRules.shadow.min_visits}</b></div>
            <div className="text-gray-700">Min days: <b>{trainingRules.shadow.min_days}</b></div>
          </div>

          <div className="border rounded-lg p-2 bg-gray-50">
            <div className="font-semibold text-gray-800 mb-1">Supervised → Full</div>
            <div className="text-gray-700">Min visits: <b>{trainingRules.supervised.min_visits}</b></div>
            <div className="text-gray-700">Min days: <b>{trainingRules.supervised.min_days}</b></div>
          </div>

          <div className="border rounded-lg p-2 bg-gray-50">
            <div className="font-semibold text-gray-800 mb-1">Exposure</div>
            <div className="text-gray-700">Min visits / 30d: <b>{trainingRules.min_monthly_visits}</b></div>
            <div className="text-[11px] text-gray-500 mt-1">Low exposure triggers after 7+ days in stage.</div>
          </div>

          <div className="border rounded-lg p-2 bg-gray-50">
            <div className="font-semibold text-gray-800 mb-1">Stuck detection</div>
            <div className="text-gray-700">Stuck after days: <b>{trainingRules.stuck_after_days}</b></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-1 bg-white rounded-xl shadow-sm border p-3 space-y-2 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-lg">Mentors</h2>
            {loadingMentors && <span className="text-xs text-gray-500">Loading...</span>}
          </div>

          {mentors.length === 0 && !loadingMentors && (
            <p className="text-sm text-gray-500">No mentors with trainees yet.</p>
          )}

          {mentors.map((m) => (
            <button
              key={m.mentor_id}
              onClick={() => setSelectedMentorId(m.mentor_id)}
              className={
                'w-full text-left px-3 py-2 rounded-lg mb-1 border ' +
                (selectedMentorId === m.mentor_id
                  ? 'bg-blue-50 border-blue-400'
                  : 'bg-white hover:bg-gray-50 border-gray-200')
              }
            >
              <div className="font-medium">{m.mentor_name}</div>
              <div className="text-xs text-gray-600 flex gap-2 mt-1">
                <span>Total: {m.total_mentees}</span>
                <span>Shadow: {m.shadow_count}</span>
                <span>Supervised: {m.supervised_count}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="md:col-span-3 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border p-3">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-semibold text-lg">Trainees for selected mentor</h2>
              {loadingTrainees && <span className="text-xs text-gray-500">Loading...</span>}
            </div>

            {trainees.length === 0 && !loadingTrainees && (
              <p className="text-sm text-gray-500">Select a mentor to see their trainees.</p>
            )}

            {trainees.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="px-2 py-2 text-left">Teacher</th>
                      <th className="px-2 py-2 text-left">Stage</th>
                      <th className="px-2 py-2 text-left">Training start</th>
                      <th className="px-2 py-2 text-center">Visits 7d</th>
                      <th className="px-2 py-2 text-center">Visits 30d</th>
                      <th className="px-2 py-2 text-center">Visits in stage</th>
                      <th className="px-2 py-2 text-center">Days in stage</th>
                      <th className="px-2 py-2 text-center">Mentor reviews</th>
                      <th className="px-2 py-2 text-left">Flag</th>
                      <th className="px-2 py-2 text-left">Rules status</th>
                      <th className="px-2 py-2 text-left">Progress</th>
                      <th className="px-2 py-2 text-center">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainees.map((t) => {
                      const mainFlag = t.primary_flag || (t.flags && t.flags[0]) || 'ok';

                      const r =
                        rulesByTraineeId.get(t.id) ||
                        deriveRulesStatus(t, trainingRules);

                      const rulesFlag = r.flag;

                      const trainingVisits = t.training_visits || 0;
                      const reviewedTrainingVisits = t.reviewed_training_visits || 0;
                      const avgRating = t.avg_rating != null ? Number(t.avg_rating).toFixed(1) : null;
                      const flaggedCount = t.flagged_count || 0;

                      const trainingStartStr = formatDateYYYYMMDD(t.training_start_date);

                      const needV = r.needVisits || 0;
                      const needD = r.needDays || 0;
                      const vOK = needV ? r.visitsInStage >= needV : true;
                      const dOK = needD ? r.days >= needD : true;

                      return (
                        <tr
                          key={t.id}
                          className={'border-b hover:bg-gray-50 ' + (selectedTrainee?.id === t.id ? 'bg-blue-50/40' : '')}
                        >
                          <td className="px-2 py-2">
                            <div className="font-medium">{t.name}</div>
                            <div className="text-xs text-gray-500">{t.email}</div>
                          </td>

                          <td className="px-2 py-2">
                            <span
                              className={
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                                (t.training_stage === 'shadow'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-blue-100 text-blue-800')
                              }
                            >
                              {t.training_stage}
                            </span>
                          </td>

                          <td className="px-2 py-2">
                            {trainingStartStr ? (
                              <div className="flex flex-col text-xs">
                                <span>{trainingStartStr}</span>
                                <span className="text-gray-500">{t.days_in_stage || 0} days in stage</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>

                          <td className="px-2 py-2 text-center">{t.visits_last_7 || 0}</td>
                          <td className="px-2 py-2 text-center">{t.visits_last_30 || 0}</td>
                          <td className="px-2 py-2 text-center">{t.visits_in_stage || 0}</td>
                          <td className="px-2 py-2 text-center">{t.days_in_stage || 0}</td>

                          <td className="px-2 py-2 text-center">
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-xs">
                                {trainingVisits} feedback entries
                                {reviewedTrainingVisits ? ` / ${reviewedTrainingVisits} rated` : ''}
                              </span>
                              <span className="text-xs text-gray-600">{avgRating ? `Avg ${avgRating}/5` : 'No rating yet'}</span>
                              {flaggedCount > 0 && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700 mt-0.5">
                                  ⚑ {flaggedCount} flagged
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="px-2 py-2">
                            <span
                              className={
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                                flagColor(mainFlag)
                              }
                              title={`primary_flag: ${mainFlag}`}
                            >
                              {flagLabel(mainFlag)}
                            </span>
                          </td>

                          <td className="px-2 py-2">
                            <div className="flex flex-col gap-1">
                              <span
                                className={
                                  'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                                  flagColor(rulesFlag)
                                }
                              >
                                {flagLabel(rulesFlag)}
                              </span>
                              {rulesFlag === 'low_exposure' && (
                                <span className="text-[10px] text-gray-500">
                                  {r.visits30}/{r.minMonthly} visits (30d)
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="px-2 py-2">
                            {(needV || needD) ? (
                              <div className="text-xs">
                                <div className="flex items-center gap-2">
                                  <span className={'px-1.5 py-0.5 rounded-full text-[10px] font-semibold ' + (vOK ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>
                                    Visits {r.visitsInStage}/{needV}
                                  </span>
                                  <span className={'px-1.5 py-0.5 rounded-full text-[10px] font-semibold ' + (dOK ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>
                                    Days {r.days}/{needD}
                                  </span>
                                </div>
                                <div className="text-[10px] text-gray-400 mt-1">Thresholds for current stage</div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>

                          <td className="px-2 py-2 text-center">
                            <button
                              className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                              onClick={() => setSelectedTrainee(t)}
                            >
                              Visits
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {trainees.length > 0 && (
              <div className="mt-2 text-[11px] text-gray-500">
                “Visits 7d/30d/in stage” = attended complete visits (training attendance + independent). “Mentor reviews” = entries from <b>teacher_training_visits</b>.
              </div>
            )}
          </div>

          {selectedTrainee && (
            <div className="bg-white rounded-xl shadow-sm border p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-lg">Visits – {selectedTrainee.name}</h3>
                  <p className="text-xs text-gray-500">
                    Stage: {selectedTrainee.training_stage} · Mentor: {selectedTrainee.mentor_name}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    Training start: {formatDateYYYYMMDD(selectedTrainee.training_start_date) || 'N/A'} · {selectedTrainee.days_in_stage || 0} days in current stage
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Showing only visits where mentor entered feedback in the training form.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span>Period:</span>
                  <select
                    className="border rounded px-2 py-1 text-xs"
                    value={visitsPeriod}
                    onChange={(e) => setVisitsPeriod(e.target.value)}
                  >
                    <option value="this_week">This week</option>
                    <option value="this_month">This month</option>
                    <option value="all">All</option>
                  </select>
                  {loadingVisits && <span className="text-gray-500">Loading...</span>}
                </div>
              </div>

              {traineeVisits.length === 0 && !loadingVisits && (
                <p className="text-sm text-gray-500">No visits with mentor feedback in selected period.</p>
              )}

              {traineeVisits.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="px-2 py-2 text-left">Date / Time</th>
                        <th className="px-2 py-2 text-left">Student</th>
                        <th className="px-2 py-2 text-left">Discipline</th>
                        <th className="px-2 py-2 text-left">Status</th>
                        <th className="px-2 py-2 text-left">Stage @ visit</th>
                        <th className="px-2 py-2 text-left">Mentor review</th>
                        <th className="px-2 py-2 text-left">Promotion / Flag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {traineeVisits.map((v) => {
                        const dateStr = v.scheduled_at ? formatDateYYYYMMDD(v.scheduled_at) : '';
                        const timeRange =
                          v.from_hhmm && v.to_hhmm ? `${v.from_hhmm}–${v.to_hhmm}` : v.from_hhmm || '';

                        return (
                          <tr key={v.id} className="border-b">
                            <td className="px-2 py-2">
                              <div className="flex flex-col">
                                <span>{dateStr}</span>
                                {timeRange && <span className="text-xs text-gray-500">{timeRange}</span>}
                              </div>
                            </td>
                            <td className="px-2 py-2">{v.first_name} {v.last_name}</td>
                            <td className="px-2 py-2">{v.service_type || v.discipline_code || '-'}</td>
                            <td className="px-2 py-2">
                              <span
                                className={
                                  'px-2 py-0.5 rounded-full text-xs ' +
                                  (v.status === 'complete'
                                    ? 'bg-green-100 text-green-700'
                                    : v.status === 'canceled'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-700')
                                }
                              >
                                {v.status}
                              </span>
                            </td>
                            <td className="px-2 py-2">
                              {v.stage_at_visit ? (
                                <span
                                  className={
                                    'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' +
                                    (v.stage_at_visit === 'shadow'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-blue-100 text-blue-800')
                                  }
                                >
                                  {v.stage_at_visit}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              {v.mentor_rating != null || v.mentor_level ? (
                                <div className="flex flex-col gap-0.5">
                                  {v.mentor_rating != null && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                                      Rating: {v.mentor_rating}/5
                                    </span>
                                  )}
                                  {v.mentor_level && (
                                    <span className="text-xs text-gray-600">
                                      Level: {String(v.mentor_level).replace(/_/g, ' ').toLowerCase()}
                                    </span>
                                  )}
                                  {v.mentor_comment && (
                                    <span className="text-[11px] text-gray-500 line-clamp-2">“{v.mentor_comment}”</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">No review</span>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex flex-col gap-0.5">
                                {v.suggest_promotion && v.suggest_promotion !== 'none' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                                    {v.suggest_promotion === 'to_supervised'
                                      ? 'Promote to supervised'
                                      : v.suggest_promotion === 'to_independent'
                                      ? 'Ready for independent'
                                      : v.suggest_promotion}
                                  </span>
                                )}
                                {v.is_flagged ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                                    ⚑ Flagged by mentor
                                  </span>
                                ) : null}
                                {!v.is_flagged && (!v.suggest_promotion || v.suggest_promotion === 'none') && (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}




